import { useQuery } from '@tanstack/react-query';
import { useContext } from 'react';
import { useWatch } from 'react-hook-form';

import { useCartStore, CartContext } from '@/entities/cart';
import { QueryApi } from '@/features/query/api';
import { DeliveryZonesModalContext } from '@/features/select-venue/ui/delivery-zones-modal';

const parseCoords = (lat?: string, lon?: string) => {
    const parsedLat = parseFloat(lat ?? '');
    const parsedLon = parseFloat(lon ?? '');
    return Number.isFinite(parsedLat) && Number.isFinite(parsedLon)
        ? { lat: parsedLat, lon: parsedLon }
        : null;
};

export const useDeliveryZonesDisplay = () => {
    const context = useContext(DeliveryZonesModalContext);
    const { formMethods } = useContext(CartContext);
    const [lat, lon] = useWatch({
        control: formMethods.control,
        name: ['deliveryMethod.address.lat', 'deliveryMethod.address.lon'],
    });
    const coords = parseCoords(lat, lon);
    const matchedArea = useCartStore((s) => s.matchedArea);
    const selectedDeliveryMethod = useCartStore((s) => s.deliveryMethod);
    const deliveryMethodsQuery = useQuery(QueryApi.DeliveryMethods.queries.DeliveryMethods);

    const allZones = (deliveryMethodsQuery.data ?? [])
        .flatMap((deliveryMethod) =>
            (deliveryMethod.areas ?? [])
                .filter((area) => area.public)
                .map((area) => ({ area, deliveryMethod })),
        )
        .sort((a, b) => (a.area.sortIndex ?? 0) - (b.area.sortIndex ?? 0));

    const userZone =
        coords && matchedArea && selectedDeliveryMethod
            ? { area: matchedArea, deliveryMethod: selectedDeliveryMethod }
            : null;

    const shouldPersonalize = userZone !== null;
    const showAllZones = !shouldPersonalize || (context?.showAllZones ?? false);
    const visibleZones = showAllZones ? allZones : userZone ? [userZone] : allZones;
    const canSelectZones = Boolean(context) && showAllZones && allZones.length > 1;
    const selectedZoneKey = canSelectZones ? (context?.selectedZoneKey ?? null) : null;

    return {
        allZones,
        userZone,
        visibleZones,
        coords,
        shouldPersonalize,
        showAllZones,
        setShowAllZones: context?.setShowAllZones,
        selectedZoneKey,
        setSelectedZoneKey: context?.setSelectedZoneKey,
        toggleSelectedZone: context?.toggleSelectedZone,
        canSelectZones,
        isLoading: deliveryMethodsQuery.isLoading,
    };
};
